import { Form, Formik, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { RichTextField } from "@/components/common/rich-text-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  createLetterTemplate,
  LETTER_RECIPIENT_OPTIONS,
  type LetterTemplatePayload,
  updateLetterTemplate,
} from "@/features/letters/api";
import type { LetterTemplate } from "@/features/letters/types";
import { SelectField } from "@/features/settings/components/select-field";
import { applyApiErrors } from "@/lib/form-errors";

const schema = Yup.object({
  name: Yup.string().trim().required("Name is required").max(120),
  recipient: Yup.string().required(),
  subject: Yup.string().trim().required("Subject is required").max(200),
  body: Yup.string().trim().required("Body is required"),
});

interface FormValues {
  name: string;
  recipient: string;
  subject: string;
  body: string;
}

interface LetterTemplateDialogProps {
  template?: LetterTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (t: LetterTemplate) => void;
}

export function LetterTemplateDialog({
  template,
  open,
  onOpenChange,
  onSaved,
}: LetterTemplateDialogProps) {
  const isEdit = !!template;

  async function handleSubmit(values: FormValues, { setErrors }: FormikHelpers<FormValues>) {
    const payload: LetterTemplatePayload = values;
    try {
      const saved = isEdit
        ? await updateLetterTemplate(template.id, payload)
        : await createLetterTemplate(payload);
      onSaved(saved);
      onOpenChange(false);
      toast.success(isEdit ? "Template updated" : "Template created");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save the template." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit template" : "New letter template"}</DialogTitle>
          <DialogDescription>
            Reusable letter your team can generate for any client.
          </DialogDescription>
        </DialogHeader>
        <Formik<FormValues>
          initialValues={{
            name: template?.name ?? "",
            recipient: template?.recipient ?? "CLIENT",
            subject: template?.subject ?? "",
            body: template?.body ?? "",
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField name="name" label="Template Name" />
                <SelectField
                  name="recipient"
                  label="Recipient"
                  options={LETTER_RECIPIENT_OPTIONS}
                />
              </div>
              <FormField name="subject" label="Subject" />
              <RichTextField
                name="body"
                label="Body"
                minHeight={180}
                hint={
                  <>
                    Use {"{client_name}"}, {"{date}"}, … placeholders to merge client data.
                  </>
                }
              />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Create Template"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
